/**
 * Announcement Bar Fade Effect
 * Handles the fade in/out transitions for announcement bar items
 */
class AnnouncementBarFade {
  constructor(container, options = {}) {
    this.container = container;
    this.items = container.querySelectorAll('.fade-effect__item');
    this.currentIndex = 0;
    this.isTransitioning = false;
    
    // Default options
    this.options = {
      autoplay: options.autoplay !== false, // Default to true
      interval: options.interval || 4000, // 4 seconds default
      fadeSpeed: options.fadeSpeed || 800, // 0.8 seconds default
      pauseOnHover: options.pauseOnHover !== false // Default to true
    };
    
    this.intervalId = null;
    
    this.init();
  }
  
  init() {
    if (this.items.length <= 1) {
      // If only one item or no items, show the first one and don't start rotation
      if (this.items.length === 1) {
        this.items[0].style.display = 'block';
        this.items[0].classList.add('active');
      }
      return;
    }
    
    // Hide all items first
    this.items.forEach(item => {
      item.style.display = 'none';
    });
    
    // Show first item
    this.items[0].style.display = 'block';
    this.items[0].classList.add('active');
    
    // Start autoplay if enabled
    if (this.options.autoplay) {
      this.startAutoplay();
    }
    
    // Add hover pause functionality
    if (this.options.pauseOnHover) {
      this.container.addEventListener('mouseenter', () => this.pauseAutoplay());
      this.container.addEventListener('mouseleave', () => this.resumeAutoplay());
    }
  }
  
  showItem(index) {
    if (this.isTransitioning || index === this.currentIndex) {
      return;
    }
    
    this.isTransitioning = true;
    
    const currentItem = this.items[this.currentIndex];
    const newItem = this.items[index];
    
    // Step 1: Fade out the active item
    currentItem.classList.remove('active');
    
    // Step 2: After fade out completes, hide old item and show new item
    setTimeout(() => {
      // Hide the old item with display: none
      currentItem.style.display = 'none';
      
      // Show the new item (display: block)
      newItem.style.display = 'block';
      // Force reflow to ensure display change is applied
      newItem.offsetHeight;
      
      // Step 3: Fade in the new item
      newItem.classList.add('active');
      
      // Update current index
      this.currentIndex = index;
      
      // Mark transition as complete after fade in
      setTimeout(() => {
        this.isTransitioning = false;
      }, this.options.fadeSpeed);
    }, this.options.fadeSpeed);
  }
  
  next() {
    const nextIndex = (this.currentIndex + 1) % this.items.length;
    this.showItem(nextIndex);
  }
  
  previous() {
    const prevIndex = this.currentIndex === 0 ? this.items.length - 1 : this.currentIndex - 1;
    this.showItem(prevIndex);
  }
  
  startAutoplay() {
    if (this.items.length <= 1) return;
    
    this.intervalId = setInterval(() => {
      this.next();
    }, this.options.interval);
  }
  
  pauseAutoplay() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
  
  resumeAutoplay() {
    if (this.options.autoplay && !this.intervalId) {
      this.startAutoplay();
    }
  }
  
  destroy() {
    this.pauseAutoplay();
    
    // Remove event listeners
    this.container.removeEventListener('mouseenter', () => this.pauseAutoplay());
    this.container.removeEventListener('mouseleave', () => this.resumeAutoplay());
  }
}

// Initialize fade effect when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  const fadeContainers = document.querySelectorAll('.announcement-bar--fade-effect .fade-effect');
  
  fadeContainers.forEach(container => {
    // Get autoplay settings from the announcement bar section
    const announcementBar = container.closest('.announcement-bar--inner');
    const fadeEffectContainer = announcementBar ? announcementBar.querySelector('.fade-effect-container') : null;
    
    let autoplayInterval = 4000; // Default 4 seconds
    
    // If there's a slide-show element, try to get autoplay settings from it
    if (fadeEffectContainer && fadeEffectContainer.dataset.autoplay) {
      const autoplayValue = parseInt(fadeEffectContainer.dataset.autoplay);
      if (autoplayValue > 0) {
        autoplayInterval = autoplayValue;
      }
    }
    
    // Initialize the fade effect
    new AnnouncementBarFade(container, {
      autoplay: true,
      interval: autoplayInterval,
      fadeSpeed: 800,
      pauseOnHover: true
    });
  });
});
